import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { listEvaluations, deleteEvaluation } from "../services/evaluation";
import { listFrequencies, listSubjectsByLevel } from "../services/teacher";
import { useConfirm } from "../hook/useConfirm";
import { levelKey } from "../utils/classesLabels";
import { isCambridgeSucursal } from "../services/auth";
import { CAMBRIDGE_CATEGORIES } from "../utils/cambridgeGrading";

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: currentYear + 1 - 2024 + 1 }, (_, i) => 2024 + i);
// Values match sge_infantil_web's `shifts` (src/pages/grade/list/index.js)
// and every gradebook/evaluation record migrated from it — real data uses
// these full labels as the `period` value, not "1"/"2"/"3".
const PERIOD_OPTIONS = [
  { value: "Primeiro Trimestre", label: "1º Trimestre" },
  { value: "Segundo Trimestre", label: "2º Trimestre" },
  { value: "Terceiro Trimestre", label: "3º Trimestre" },
];

// Split out of the former AvaliacoesLayer (which mixed the wizard with the
// evaluation table). Mirrors ListaProdutosLayer/ListaDisciplinasLayer: the
// create screen is hidden from the menu and only reachable through the
// "Adicionar Avaliação" button here — see CadastrarAvaliacaoLayer / App.js
// route `/academico/avaliacoes/cadastrar`. The current level/subject/year/
// period selection is carried along via navigation state, both when going
// to the create screen and when coming back, so the context isn't lost.
const ListaAvaliacoesLayer = () => {
  const { t } = useTranslation();
  const { confirm, ConfirmModal } = useConfirm();
  const navigate = useNavigate();
  const location = useLocation();
  const carried = location.state || {};

  const [frequencies, setFrequencies] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [levelId, setLevelId] = useState(carried.levelId || "");
  const [subjectId, setSubjectId] = useState(carried.subjectId || "");
  const [year, setYear] = useState(carried.year || currentYear);
  const [period, setPeriod] = useState(carried.period || "Primeiro Trimestre");

  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const isCambridge = isCambridgeSucursal();
  const categoryLabel = (ev) => CAMBRIDGE_CATEGORIES.find((c) => c.value === ev.category)?.label || ev.evaluationType || "-";

  useEffect(() => {
    listFrequencies().then((resp) => setFrequencies(resp.data || [])).catch(() => setFrequencies([]));
  }, []);

  useEffect(() => {
    if (!levelId) {
      setSubjects([]);
      return;
    }
    listSubjectsByLevel(levelId).then((resp) => setSubjects(resp.data || [])).catch(() => setSubjects([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelId]);

  const fetchEvaluations = () => {
    if (!levelId || !subjectId) {
      setEvaluations([]);
      return;
    }
    setLoading(true);
    listEvaluations(levelId, subjectId)
      .then((resp) =>
        setEvaluations(
          (resp.data || []).filter((ev) => String(ev.period) === period && String(ev.year) === String(year)),
        ),
      )
      .catch(() => toast.error(t("avaliacao.error.load")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchEvaluations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelId, subjectId, year, period]);

  const totalWeight = useMemo(
    () => evaluations.reduce((sum, ev) => sum + (Number(ev.weight) || 0), 0),
    [evaluations],
  );

  const handleLevelChange = (e) => {
    setLevelId(e.target.value);
    setSubjectId("");
  };

  const handleDelete = async (ev) => {
    if (!(await confirm(t("avaliacao.confirm.delete", { name: ev.evaluationName })))) return;
    setDeletingId(ev.id);
    try {
      await deleteEvaluation(ev.id);
      toast.success(t("avaliacao.success.deleted"));
      fetchEvaluations();
    } catch (err) {
      toast.error(t("avaliacao.error.delete"));
    } finally {
      setDeletingId(null);
    }
  };

  const goToCreate = () => {
    navigate("/academico/avaliacoes/cadastrar", { state: { levelId, subjectId, year, period } });
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />

      {subjectId && (
        <div className='col-12'>
          <div className='d-flex flex-wrap align-items-center gap-3'>
            <div className='card flex-fill radius-12 border-0 shadow-sm'>
              <div className='card-body d-flex align-items-center gap-3 py-16 px-20'>
                <span className='w-44-px h-44-px radius-12 bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center text-xl'>
                  <Icon icon='mdi:clipboard-list-outline' />
                </span>
                <div>
                  <h6 className='mb-0'>{evaluations.length}</h6>
                  <span className='text-secondary-light text-sm'>
                    {t("avaliacao.list.stat.total")}
                  </span>
                </div>
              </div>
            </div>
            <div className='card flex-fill radius-12 border-0 shadow-sm'>
              <div className='card-body d-flex align-items-center gap-3 py-16 px-20'>
                <span
                  className={`w-44-px h-44-px radius-12 d-flex align-items-center justify-content-center text-xl ${
                    totalWeight === 100
                      ? "bg-success-focus text-success-main"
                      : "bg-warning-focus text-warning-main"
                  }`}
                >
                  <Icon icon='solar:pie-chart-2-outline' />
                </span>
                <div>
                  <h6 className='mb-0'>{totalWeight}%</h6>
                  <span className='text-secondary-light text-sm'>
                    {t("avaliacao.list.stat.totalWeight")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24'>
              <div>
                <h6 className='mb-4'>{t("avaliacao.section.list")}</h6>
                <p className='text-secondary-light mb-0 text-sm'>{t("avaliacao.list.subtitle")}</p>
              </div>
              <button
                type='button'
                className='btn btn-primary-600 d-flex align-items-center gap-2'
                onClick={goToCreate}
              >
                <Icon icon='ic:baseline-plus' />
                {t("avaliacao.list.add")}
              </button>
            </div>

            <div className='p-16 radius-8 bg-neutral-50 mb-24'>
              <div className='row gy-3 align-items-end'>
                <div className='col-sm-6 col-lg-3'>
                  <label className='form-label text-sm mb-8'>{t(levelKey("turma.field.educationLevel"))}</label>
                  <select className='form-control form-select' value={levelId} onChange={handleLevelChange}>
                    <option value=''>{t(levelKey("turma.field.educationLevel.placeholder"))}</option>
                    {frequencies.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.description}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6 col-lg-3'>
                  <label className='form-label text-sm mb-8'>{t("disciplina.field.subjectName")}</label>
                  <select
                    className='form-control form-select'
                    value={subjectId}
                    onChange={(e) => setSubjectId(e.target.value)}
                    disabled={!levelId}
                  >
                    <option value=''>{t("avaliacao.field.subject.placeholder")}</option>
                    {subjects.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.subjectName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6 col-lg-3'>
                  <label className='form-label text-sm mb-8'>{t("turma.field.academicYear")}</label>
                  <select
                    className='form-control form-select'
                    value={year}
                    onChange={(e) => setYear(Number(e.target.value))}
                  >
                    {YEAR_OPTIONS.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6 col-lg-3'>
                  <label className='form-label text-sm mb-8'>{t("avaliacao.field.period")}</label>
                  <select
                    className='form-control form-select'
                    value={period}
                    onChange={(e) => setPeriod(e.target.value)}
                  >
                    {PERIOD_OPTIONS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {!subjectId ? (
              <div className='alert alert-info mb-0'>{t(levelKey("avaliacao.hint.selectSubject"))}</div>
            ) : (
              <>
                <div className='table-responsive scroll-sm'>
                  <table className='table bordered-table mb-0'>
                    <thead>
                      <tr>
                        <th scope='col'>{t("avaliacao.field.name")}</th>
                        <th scope='col'>{t("disciplina.field.subjectName")}</th>
                        <th scope='col'>{isCambridge ? t("avaliacao.field.category") : t("avaliacao.field.type")}</th>
                        <th scope='col'>{t("avaliacao.field.weight")}</th>
                        <th scope='col' className='text-end'>
                          {t("turma.col.actions")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading && (
                        <tr>
                          <td colSpan={5} className='text-center text-secondary-light py-32'>
                            {t("global.loading")}
                          </td>
                        </tr>
                      )}
                      {!loading && evaluations.length === 0 && (
                        <tr>
                          <td colSpan={5} className='text-center text-secondary-light py-32'>
                            {t("avaliacao.hint.noEvaluations")}
                          </td>
                        </tr>
                      )}
                      {evaluations.map((ev) => (
                        <tr key={ev.id}>
                          <td className='fw-semibold text-primary-light'>{ev.evaluationName}</td>
                          <td>{ev.subject?.name || "-"}</td>
                          <td>{isCambridge ? categoryLabel(ev) : ev.evaluationType}</td>
                          <td>{ev.weight}%</td>
                          <td className='text-end'>
                            <button
                              type='button'
                              className='btn btn-outline-danger-600 btn-sm d-flex align-items-center gap-1 ms-auto'
                              disabled={deletingId === ev.id}
                              onClick={() => handleDelete(ev)}
                            >
                              <Icon icon='mdi:trash-can-outline' />
                              {t("global.delete")}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!loading && evaluations.length > 0 && (
                  <p className='text-secondary-light text-sm mt-16 mb-0'>
                    {t("avaliacao.hint.totalWeight", { total: totalWeight })}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {ConfirmModal}
    </div>
  );
};

export default ListaAvaliacoesLayer;
